'use client'

import {useEffect, useState} from 'react'
import {Mail, Save, Send, ShieldCheck} from 'lucide-react'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {message} from '@/components/Toast'
import {apiGet, apiPost, apiPut} from '@/lib/api'

type RegistrationSettings = {
    enabled: boolean
    trialDays: number
    loginCaptchaEnabled: boolean
    smtpHost: string
    smtpPort: number
    smtpUsername: string
    smtpPassword: string
    smtpPasswordSet: boolean
    smtpFromEmail: string
    smtpFromName: string
    smtpUseTLS: boolean
}

const emptySettings: RegistrationSettings = {
    enabled: false,
    trialDays: 7,
    loginCaptchaEnabled: true,
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpPasswordSet: false,
    smtpFromEmail: '',
    smtpFromName: '微信机器人系统',
    smtpUseTLS: true,
}

export function RegistrationSettingsView() {
    const [draft, setDraft] = useState<RegistrationSettings>(emptySettings)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [testEmail, setTestEmail] = useState('')

    useEffect(() => {
        apiGet<{success: boolean; settings: RegistrationSettings}>('/registration/settings', {silent: true})
            .then((res) => {
                if (res.success && res.settings) {
                    setDraft({...emptySettings, ...res.settings, smtpPassword: ''})
                    setTestEmail(res.settings.smtpFromEmail || '')
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [])

    async function handleSave() {
        const trialDays = Math.min(365, Math.max(1, Number(draft.trialDays) || 7))
        setSaving(true)
        try {
            const res = await apiPut<{success: boolean; settings: RegistrationSettings}>('/registration/settings', {
                ...draft,
                trialDays,
            })
            if (res.success) {
                setDraft({...emptySettings, ...res.settings, smtpPassword: ''})
                message.success('注册设置已保存')
            }
        } finally {
            setSaving(false)
        }
    }

    async function handleTestSMTP() {
        setTesting(true)
        try {
            const res = await apiPost<{success: boolean; message?: string}>('/registration/settings/test-smtp', {
                ...draft,
                testEmail: testEmail.trim(),
            })
            if (res.success) message.success(res.message || '测试邮件已发送')
        } finally {
            setTesting(false)
        }
    }

    return (
        <section className="flex h-full flex-col bg-page">
            <PageHeader title="注册设置" description="配置试用账号注册、邮箱验证码与试用有效期"/>
            <div className="flex-1 overflow-y-auto p-6">
                <div className="mr-auto w-full max-w-4xl">
                    <div className="rounded-card border border-hair bg-white p-8 shadow-card">
                        {loading ? (
                            <div className="py-10 text-center text-sm text-muted">加载中...</div>
                        ) : (
                            <div className="space-y-8">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <div className="mb-2 flex items-center gap-2 text-base font-medium text-ink">
                                            <ShieldCheck size={18} className="text-accent"/>
                                            开放试用账号注册
                                        </div>
                                        <p className="text-sm leading-relaxed text-muted">
                                            开启后，登录页会显示试用注册入口，用户通过邮箱验证码创建普通试用账号。
                                        </p>
                                    </div>
                                    <Switch
                                        checked={draft.enabled}
                                        onChange={() => setDraft({...draft, enabled: !draft.enabled})}
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-5 border-t border-hair pt-6 md:grid-cols-2">
                                    <Field label="试用有效期（天）">
                                        <input
                                            type="number"
                                            min={1}
                                            max={365}
                                            className="input"
                                            value={draft.trialDays}
                                            onChange={(event) => setDraft({...draft, trialDays: Number(event.target.value) || 1})}
                                        />
                                    </Field>
                                </div>

                                <div className="flex items-center justify-between gap-4 border-t border-hair pt-6">
                                    <div>
                                        <div className="text-sm font-medium text-ink">登录滑块验证</div>
                                        <div className="mt-1 text-xs text-muted">开启后，登录时需要完成滑块安全验证；关闭后直接使用账号密码登录。</div>
                                    </div>
                                    <Switch
                                        checked={draft.loginCaptchaEnabled}
                                        onChange={() => setDraft({...draft, loginCaptchaEnabled: !draft.loginCaptchaEnabled})}
                                    />
                                </div>

                                <div className="border-t border-hair pt-6">
                                    <div className="mb-5 flex items-center gap-2 text-base font-medium text-ink">
                                        <Mail size={18} className="text-accent"/>
                                        邮箱验证码 SMTP 配置
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                        <Field label="SMTP 服务器">
                                            <input
                                                className="input"
                                                value={draft.smtpHost}
                                                placeholder="smtp.example.com"
                                                onChange={(event) => setDraft({...draft, smtpHost: event.target.value})}
                                            />
                                        </Field>
                                        <Field label="SMTP 端口">
                                            <input
                                                type="number"
                                                className="input"
                                                value={draft.smtpPort}
                                                onChange={(event) => setDraft({...draft, smtpPort: Number(event.target.value) || 587})}
                                            />
                                        </Field>
                                        <Field label="SMTP 登录账号">
                                            <input
                                                className="input"
                                                value={draft.smtpUsername}
                                                placeholder="mail@example.com"
                                                onChange={(event) => setDraft({...draft, smtpUsername: event.target.value})}
                                            />
                                        </Field>
                                        <Field label={draft.smtpPasswordSet ? 'SMTP 密码/授权码（已配置，留空不修改）' : 'SMTP 密码/授权码'}>
                                            <input
                                                type="password"
                                                className="input"
                                                value={draft.smtpPassword}
                                                onChange={(event) => setDraft({...draft, smtpPassword: event.target.value})}
                                            />
                                        </Field>
                                        <Field label="发件邮箱">
                                            <input
                                                className="input"
                                                value={draft.smtpFromEmail}
                                                placeholder="mail@example.com"
                                                onChange={(event) => setDraft({...draft, smtpFromEmail: event.target.value})}
                                            />
                                        </Field>
                                        <Field label="发件名称">
                                            <input
                                                className="input"
                                                value={draft.smtpFromName}
                                                onChange={(event) => setDraft({...draft, smtpFromName: event.target.value})}
                                            />
                                        </Field>
                                    </div>
                                    <div className="mt-5 flex items-center justify-between rounded border border-hair bg-gray-50 p-4">
                                        <div>
                                            <div className="text-sm font-medium text-ink">启用 TLS/STARTTLS</div>
                                            <div className="mt-1 text-xs text-muted">支持 587 STARTTLS 与 465 隐式 TLS。</div>
                                        </div>
                                        <Switch
                                            checked={draft.smtpUseTLS}
                                            onChange={() => setDraft({...draft, smtpUseTLS: !draft.smtpUseTLS})}
                                        />
                                    </div>
                                    <div className="mt-5 rounded border border-hair bg-white p-4">
                                        <div className="mb-3 text-sm font-medium text-ink">测试 SMTP 配置</div>
                                        <div className="flex flex-col gap-3 sm:flex-row">
                                            <input
                                                className="input h-9"
                                                value={testEmail}
                                                placeholder="测试收件邮箱，默认使用发件邮箱"
                                                onChange={(event) => setTestEmail(event.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="btn-secondary h-9 shrink-0"
                                                disabled={testing}
                                                onClick={() => void handleTestSMTP()}
                                            >
                                                <Send size={15}/>
                                                {testing ? '发送中...' : '发送测试邮件'}
                                            </button>
                                        </div>
                                        <div className="mt-2 text-xs text-muted">
                                            测试会使用当前表单中的 SMTP 配置；如果密码留空，会复用已保存的密码/授权码。
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end border-t border-hair pt-5">
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        disabled={saving}
                                        onClick={() => void handleSave()}
                                    >
                                        <Save size={16}/>
                                        {saving ? '保存中...' : '保存注册设置'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

function Field({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <label className="space-y-2">
            <span className="text-sm text-soft">{label}</span>
            {children}
        </label>
    )
}
